import { createContext, useContext } from 'react'

import { WorkflowContainerFragment, WorkflowManifestFragment } from 'generated/resources/graphql'
import { z } from 'zod'

import { EdgeUI, NodeUI } from 'modules/workflows/components/Node/types'
import {
  VariablesConfig,
  type EntityResolution,
} from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import {
  FormContext,
  NodeSidepanelSchema,
} from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { BuildingBlock } from 'modules/workflows/components/WorkflowBuildingBlocksBox'

export type TriggerConfig = { title: string; description: string }

export type { EntityResolution }

export type HandlerFormComponentPayload = { [key: string]: unknown }
export type HandlerFormComponent = {
  payload: HandlerFormComponentPayload
  onChange: (values: HandlerFormComponentPayload, isValid: boolean) => void
  readonly?: boolean
}

export type HandlersConfig = {
  [handlerName: string]: {
    dropDownLabel: string
    component: React.ComponentType<HandlerFormComponent>
    initializer: (payload: HandlerFormComponentPayload) => { [key: string]: unknown }
    beforeSave?: (
      payload: HandlerFormComponentPayload,
      handlerType: string,
      context?: {
        nodeId: string
        workflow: WorkflowContainerFragment
        nodesUI?: NodeUI[]
      }
    ) => { [key: string]: unknown }
    schema: (formContext: FormContext) => z.ZodSchema<NodeSidepanelSchema>
  }
}

export type MenuControls = {
  closeAllMenus: () => void
  registerMenu: (id: string, closeCallback: () => void) => () => void
}

type WorkflowContextProps = {
  buildingBlocks: BuildingBlock[]
  nodesUI: NodeUI[]
  setNodesUI: React.Dispatch<React.SetStateAction<NodeUI[]>>
  edgesUI: EdgeUI[]
  setEdgesUI: React.Dispatch<React.SetStateAction<EdgeUI[]>>
  isDirty: boolean
  elevateNodeZIndex: (nodeId: string) => void
  restoreNodeZIndex: (nodeId: string) => void
  triggerConfig: TriggerConfig
  workflowManifest: WorkflowManifestFragment
  workflow: WorkflowContainerFragment
  handlersConfig: HandlersConfig
  menuControls: MenuControls
  sidepanelDirty: boolean
  setSidepanelDirty: (dirty: boolean) => void
  variablesConfig: VariablesConfig
  entityResolution: EntityResolution
  /**
   * Multi Action "edit" mode: we're editing an existing parallel task.
   * When set, the sidepanel shows TaskNodeSidepanel/ApprovalNodeSidepanel for that task
   * (built from parallelTasks). On save we update the task in the array; on back we clear this.
   */
  multiActionEditingTaskId: string | null
  setMultiActionEditingTaskId: (id: string | null) => void
  /**
   * Multi Action "draft" mode: we're creating a new parallel task (not in the list yet).
   * Set when user clicks "New action" (Task/Approval) or empty state on the canvas.
   * taskId is a stable id for the new task so file uploads can use it as node_identifier.
   * Sidepanel shows Task/Approval form with default values; on save we push to parallelTasks
   * with that taskId; on cancel/back we clear this and return to the task list.
   */
  multiActionDraft: { nodeId: string; type: 'task' | 'approvals'; taskId: string } | null
  setMultiActionDraft: (
    draft: {
      nodeId: string
      type: 'task' | 'approvals'
      taskId: string
    } | null
  ) => void
  readonly: boolean
}

export const WorkflowContext = createContext<WorkflowContextProps | null>(null)

export const useWorkflowContext = (): WorkflowContextProps => {
  const context = useContext(WorkflowContext)
  if (context === null) {
    throw new Error('useWorkflowContext must be used within a WorkflowContext.Provider')
  }
  return context
}
